import { Component, computed, inject, input, LOCALE_ID, signal } from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatGridListModule } from '@angular/material/grid-list';
import { Router, RouterLink, ActivatedRoute, RouterOutlet } from '@angular/router';
import { Project, Assessment } from '../models';
import { ProjectDomainService } from '../domain-services/project-domain.service';
import { UserPermissionsService } from '../user-permissions.service';
import { AssessmentDomainService } from '../domain-services/assessment-domain.service';
import { countBy, filter, groupBy, map, orderBy, size } from 'lodash-es';
import { MatDialog } from '@angular/material/dialog';
import { catchError, of } from 'rxjs';

@Component({
  selector: 'app-project-list',
  imports: [
    MatToolbarModule,
    MatButtonToggleModule,
    MatIconModule,
    MatButtonModule,
    MatGridListModule,
    RouterLink,
    RouterOutlet,
  ],
  templateUrl: './project-list.html',
  styleUrl: './project-list.scss',
})
export class ProjectList {
  readonly locale = inject(LOCALE_ID);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly dialog = inject(MatDialog);

  readonly permissions = inject(UserPermissionsService);
  private readonly projectDomain = inject(ProjectDomainService);
  private readonly assessmentDomain = inject(AssessmentDomainService);

  readonly projects = input<Project[]>();
  readonly assessments = input<Assessment[]>();

  readonly sortedProjects = computed(() => {
    const projects = this.projectDomain.projects() ?? this.projects() ?? [];
    const assessments = this.assessmentDomain.allAssessments() ?? this.assessments() ?? [];
    const groupedAssessments = groupBy(assessments, 'projectId');
    return orderBy(
      map(projects, (p) => {
        let projectAssessments = groupedAssessments[p.id] ?? [];

        // Count total unique participants in this project
        const participantIds = new Set(projectAssessments.map((a) => a.participantId));

        // Count participants who have ALL assessments completed (no null completedDate)
        let completedCount = 0;
        for (const participantId of participantIds) {
          const participantAssessments = projectAssessments.filter(
            (a) => a.participantId === participantId,
          );
          const hasAnyIncomplete = participantAssessments.some((a) => !a.completedDate);
          if (!hasAnyIncomplete) {
            completedCount++;
          }
        }

        return {
          ...p,
          participant: participantIds.size,
          completed: completedCount,
          createdLabel: this.formatCreated(p.created),
        };
      }),
      [this.sortBy()],
      [this.sortDirection()],
    );
  });

  // Sorting key for the project list: 'date' | 'name' | 'participant' | 'completed'
  sortBy = signal<'created' | 'name' | 'participant' | 'completed'>('created');
  // Sorting order: 'asc' or 'desc'
  sortDirection = signal<'asc' | 'desc'>('desc');
  // Relative creation labels (no auto rollover required)
  formatCreated(createdIso: string): string {
    let dayMs = 86_400_000;
    let rtf = new Intl.RelativeTimeFormat(this.locale, { numeric: 'auto' });
    const t = Date.parse(createdIso);
    const diffMs = Date.now() - t;
    if (diffMs < 0 || diffMs < dayMs) {
      // Future or today -> special label
      return $localize`Created today`;
    }
    const days = Math.floor(diffMs / dayMs);
    let value: number;
    let unit: Intl.RelativeTimeFormatUnit;
    if (days < 30) {
      value = -days;
      unit = 'day';
    } else {
      const months = Math.floor(days / 30);
      if (months < 24) {
        value = -months;
        unit = 'month';
      } else {
        const years = Math.floor(months / 12);
        value = -years;
        unit = 'year';
      }
    }
    const rel = rtf.format(value, unit); // Already localized
    // Minimal additional localization: only the word Created
    return $localize`Created` + ' ' + rel;
  }

  constructor() {
    // When viewing the top-level project list (and its children
    // like the new-project dialog), there should be no "active"
    // project. Clearing here ensures the NewProjectDialog treats
    // this as a create operation instead of a rename.
    this.projectDomain.clearCurrentProject();

    if (!this.projectDomain.projects()) {
      this.projectDomain
        .loadProjects()
        .pipe(catchError(() => of([])))
        .subscribe();
    }

    if (!this.assessmentDomain.allAssessments()) {
      this.assessmentDomain
        .loadAssessments()
        .pipe(catchError(() => of([])))
        .subscribe();
    }
  }
}
